
import { createContext, useContext, useCallback, useMemo, useEffect } from "react";
import { useAuth } from "../AuthContext";
import { Board, BoardsContextType } from "./types";
import { ensureValidBoardName, getBoardDisplayName as getDisplayName } from "./utils";
import { useBoardsQuery } from "./hooks/useBoardsQuery";
import { useBoardOperations } from "./hooks/useBoardOperations";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Create context
const BoardsContext = createContext<BoardsContextType | undefined>(undefined);

// Provider component
export const BoardsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  // Use the query hook
  const { 
    boards: rawBoards, 
    isLoading, 
    refreshBoards, 
    getBoardById, 
    updateBoardPosition 
  } = useBoardsQuery(user?.id);

  // Always ensure sorted boards with useMemo
  const boards = useMemo(() => {
    console.log("BoardsContext: Sorting boards by display_order");
    // Create a new array to avoid mutations and ensure consistent sorting
    return [...rawBoards].sort((a, b) => a.display_order - b.display_order);
  }, [rawBoards]);
  
  // Periodically refresh boards to maintain order consistency
  useEffect(() => {
    // Initial refresh
    refreshBoards();
    
    // Set up interval for periodic refresh
    const intervalId = setInterval(() => {
      console.log("BoardsContext: Periodic refresh of boards");
      refreshBoards();
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(intervalId);
  }, [refreshBoards]);

  // Use the operations hook
  const { 
    operation, 
    startEditBoard, 
    startDeleteBoard, 
    closeOperation 
  } = useBoardOperations(getBoardById);

  // Board display name helper
  const getBoardDisplayName = useCallback((board: Partial<Board>): string => {
    return getDisplayName(board, getBoardById, boards);
  }, [getBoardById, boards]);

  // Handle board updated
  const handleBoardUpdated = useCallback(() => {
    console.log("Board updated, refreshing data");
    refreshBoards();
    closeOperation();
  }, [refreshBoards, closeOperation]);

  // Handle board deleted
  const handleBoardDeleted = useCallback(() => {
    console.log("Board deleted, refreshing data");
    refreshBoards();
    closeOperation();
  }, [refreshBoards, closeOperation]);

  // Context value
  const value = {
    boards,
    isLoading,
    refreshBoards,
    operation,
    startEditBoard,
    startDeleteBoard,
    closeOperation,
    handleBoardUpdated,
    handleBoardDeleted,
    getBoardDisplayName,
    getBoardById,
    updateBoardPosition
  };

  return (
    <BoardsContext.Provider value={value}>
      {children}
    </BoardsContext.Provider>
  );
};

// Hook to use context
export const useBoards = () => {
  const context = useContext(BoardsContext);
  if (context === undefined) {
    throw new Error("useBoards must be used within a BoardsProvider");
  }
  return context;
};
